// src/pages/CI/ChatJobPreview.js
//
// The rich "about this visit" preview inside a COMMERCIAL chat bubble, and the
// popup behind it. Used by CI/CommercialChat.js; the customer's app renders the
// same two things from the same payload (sowash-commercial-app
// src/components/ChatVisitCard.tsx).
//
// Before this, a tagged message showed one grey line of text. Now it shows what
// a WhatsApp link preview shows: the site, when, who led the crew, and up to
// four photos of the actual work — then opens the whole visit on click.
//
// ── Where the data comes from ──────────────────────────────────────────────
//
// The CARD needs no request. Its fields ride along on the message itself:
// message.visit is built by shapeVisit() in sowash-backend
// utils/commercialChatVisit.js, which pre-parses the photo columns and caps the
// strip at four paths, so a 500-message thread does not carry 500 photo arrays.
//
// The POPUP fetches GET /api/commercial-chat/visits/:id/detail — a staff
// endpoint added alongside this file because none existed: every other staff
// route over site_schedules is a LIST. It deliberately mirrors the customer's
// /api/customer-portal/:id/detail column-for-column, minus the approval gate,
// so the two apps can render the same shape.
//
// ⚠ visit.pending_approval is staff-only and worth surfacing loudly: it means
// the crew finished but nobody approved it, so the CUSTOMER's app is refusing
// to open this very visit. An agent about to say "see the photos I tagged"
// needs to know the client cannot.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../helpers/auth';
import { fmtPKTDateTime } from '../../helpers/time';

const API = process.env.REACT_APP_API_BASE_URL;
// Attachments and photos come back as "/uploads/..." — the base must not repeat
// /api, which REACT_APP_API_BASE_URL already includes.
const IMG_BASE = API ? API.replace('/api', '') : '';

const T = {
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textStrong: 'var(--nd-text-strong)',
  textMuted: 'var(--nd-tx35)',
  teal: '#0F766E',
  tealLight: '#2DD4BF',
  green: '#22D3A5',
  amber: '#F59E0B',
  red: '#F87171',
  blue: '#60A5FA',
};

/**
 * One stored photo path -> a loadable URL.
 *
 * ⚠ Note the LAST branch. The web portal's own helper
 * (CustomerPortal/CustomerDashboard.js) falls through to `${IMG_BASE}${path}`,
 * which for a bare filename yields "https://app.sowashusa.comfoo.jpg" — no
 * slash. The SPA catch-all answers that with index.html and HTTP 200, so the
 * <img> fails while onError never fires. That bug hid every photo in the
 * residential app for months. Bare names resolve to /uploads/, which is where
 * they actually live.
 *
 * The backend already normalises the paths it puts in visit.photos; this covers
 * the raw before_photos/after_photos columns the detail endpoint returns.
 */
export const photoUrl = (path) => {
  if (!path) return null;
  const s = String(path).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s) || /^data:/i.test(s)) return s;

  const idx = s.indexOf('/uploads/');
  if (idx >= 0) return `${IMG_BASE}${s.substring(idx)}`;
  if (!s.includes('/')) return `${IMG_BASE}/uploads/${s}`;
  return `${IMG_BASE}${s.startsWith('/') ? '' : '/'}${s}`;
};

/**
 * before_photos / after_photos are `text` columns holding a JSON array, with
 * some legacy rows holding a single bare path instead.
 */
export const parsePhotos = (raw) => {
  if (!raw) return [];
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr.filter((p) => typeof p === 'string' && p.trim()) : [];
  } catch {
    return typeof raw === 'string' && raw.trim() ? [raw.trim()] : [];
  }
};

const photoUrlList = (raw) => parsePhotos(raw).map(photoUrl).filter(Boolean);

/**
 * scheduled_date and original_date are DATE columns, serialised ::text as
 * "YYYY-MM-DD". They carry no time of day, so they must NOT go through
 * fmtPKTDate — shifting a bare date by a timezone moves the day. Formatted
 * literally here.
 */
const fmtDateOnly = (value, fallback = '—') => {
  if (!value) return fallback;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return String(value);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
};

const norm = (s) => String(s || '').trim().toLowerCase();

const statusMeta = (status) => {
  const s = norm(status);
  if (s.startsWith('complet')) return { label: 'Completed', color: T.green };
  if (s.startsWith('cancel')) return { label: 'Cancelled', color: T.red };
  if (s === 'scheduled') return { label: 'Scheduled', color: T.blue };
  if (['started', 'in_progress', 'before_photos', 'after_photos'].includes(s)) {
    return { label: 'In Progress', color: T.amber };
  }
  return { label: status || 'Unknown', color: T.textMuted };
};

// ══════════════════════════ the bubble card ══════════════════════════

/**
 * @param visit  message.visit, as shaped by utils/commercialChatVisit.js
 * @param mine   true inside the agent's own (teal) bubble — the card has to
 *               read against a coloured background as well as a grey one
 * @param onOpen called with the schedule id when the agent asks for details
 */
export function VisitCard({ visit, mine, onOpen }) {
  if (!visit) return null;

  const meta = statusMeta(visit.status);
  const photos = Array.isArray(visit.photos) ? visit.photos : [];
  const extra = Math.max(0, (visit.photo_count || 0) - photos.length);
  const clickable = visit.can_open !== false;

  const place = [visit.address, visit.city].filter(Boolean).join(', ');

  return (
    <div
      style={{ ...C.card, ...(mine ? C.cardMine : null), cursor: clickable ? 'pointer' : 'default' }}
      onClick={clickable ? () => onOpen(visit.id) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen(visit.id);
        }
      }}
    >
      {/* Photos lead, the way a link preview does — they are the reason the
          message was tagged to a visit at all. */}
      {photos.length > 0 ? (
        <div style={{ ...C.strip, gridTemplateColumns: `repeat(${Math.min(photos.length, 4)}, 1fr)` }}>
          {photos.map((p, i) => {
            const isLast = i === photos.length - 1;
            return (
              <div key={`${p}-${i}`} style={C.thumbWrap}>
                <img src={photoUrl(p)} alt="" style={C.thumb} loading="lazy" />
                {isLast && extra > 0 ? <div style={C.more}>+{extra}</div> : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <div style={C.cardBody}>
        <div style={C.cardTop}>
          <span style={C.cardTitle}>{visit.site_name || 'Visit'}</span>
          <span style={{ ...C.dot, background: meta.color }} />
          <span style={{ ...C.cardStatus, color: meta.color }}>{meta.label}</span>
        </div>

        <div style={C.cardMeta}>
          {fmtDateOnly(visit.scheduled_date)}
          {visit.team_lead_name ? ` · ${visit.team_lead_name}` : ''}
          {visit.photo_count > 0 ? ` · ${visit.photo_count} photo${visit.photo_count === 1 ? '' : 's'}` : ''}
        </div>

        {place ? <div style={C.cardMeta}>{place}</div> : null}

        {visit.pending_approval ? (
          <div style={C.warn}>Awaiting approval — the client cannot open this visit yet</div>
        ) : null}

        {clickable ? (
          <div style={C.cardLink}>View details →</div>
        ) : (
          <div style={C.cardMutedLink}>Details not available</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════ the popup ══════════════════════════

const FINDINGS = [
  ['panel_damage', 'Panel damage'],
  ['panel_brand', 'Panel brand'],
  ['cable_condition', 'Cable condition'],
  ['cable_quantity', 'Cable quantity'],
  ['inverter_alarm', 'Inverter alarm'],
  ['alarm_code', 'Alarm code'],
  ['potential_shading', 'Shading'],
  ['shading_details', 'Shading details'],
  ['rusting', 'Rusting'],
  ['bird_dropping', 'Bird droppings'],
  ['mos_and_debris', 'Moss and debris'],
  ['earthing', 'Earthing'],
  ['cash_collected', 'Cash collected'],
];

// Booleans arrive as real booleans from the FSR table; the varchar-boolean
// columns arrive as free-form 'yes'/'Yes'/'No'. Both render as Yes/No.
const findingValue = (v) => {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim();
  if (norm(s) === 'yes') return 'Yes';
  if (norm(s) === 'no') return 'No';
  return s;
};

export function JobDetailModal({ scheduleId, onClose }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { urls, index }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    axios
      .get(`${API}/commercial-chat/visits/${scheduleId}/detail`, getAuthHeader())
      .then(({ data }) => {
        if (!alive) return;
        setJob(data?.job || null);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.error || 'Could not load this visit.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [scheduleId]);

  // Escape closes the lightbox first, then the modal — otherwise one keypress
  // dismisses both and the agent loses their place in the conversation.
  const onKey = useCallback((e) => {
    if (e.key === 'Escape') {
      if (lightbox) setLightbox(null);
      else onClose();
    }
    if (!lightbox) return;
    if (e.key === 'ArrowRight') {
      setLightbox((lb) => (lb ? { ...lb, index: (lb.index + 1) % lb.urls.length } : lb));
    }
    if (e.key === 'ArrowLeft') {
      setLightbox((lb) => (lb ? { ...lb, index: (lb.index - 1 + lb.urls.length) % lb.urls.length } : lb));
    }
  }, [lightbox, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  const before = job ? photoUrlList(job.before_photos) : [];
  const after = job ? photoUrlList(job.after_photos) : [];
  const meta = statusMeta(job?.status);

  const findings = job
    ? FINDINGS
        .map(([key, label]) => [label, job[key]])
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    : [];

  return (
    <div style={C.wrap} onClick={onClose}>
      <div style={C.modal} onClick={(e) => e.stopPropagation()}>
        <div style={C.modalHead}>
          <div style={{ minWidth: 0 }}>
            <div style={C.modalTitle}>{job?.site_name || 'Visit details'}</div>
            <div style={C.modalSub}>
              {job
                ? [job.client_name, [job.address, job.city, job.state].filter(Boolean).join(', ')]
                    .filter(Boolean)
                    .join(' · ')
                : ' '}
            </div>
          </div>
          <button style={C.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={C.modalBody}>
          {loading ? (
            <div style={C.state}>Loading…</div>
          ) : error ? (
            <div style={C.stateError}>{error}</div>
          ) : !job ? (
            <div style={C.state}>Nothing to show.</div>
          ) : (
            <>
              {/* ── facts ─────────────────────────────────────────── */}
              <div style={C.factGrid}>
                <Fact label="Status" value={meta.label} color={meta.color} />
                <Fact label="Scheduled" value={fmtDateOnly(job.scheduled_date)} />
                <Fact label="Service #" value={job.service_number ?? '—'} />
                <Fact label="Crew lead" value={job.team_lead_name || '—'} />
                <Fact label="Approval" value={job.approval_status || '—'} />
                <Fact label="Priority" value={job.priority || '—'} />
              </div>

              {job.visible_to_customer === false ? (
                <div style={C.warnBlock}>
                  Not visible to the client yet. Their app only opens a visit once it is
                  approved, or on the day it is scheduled.
                </div>
              ) : null}

              {job.reschedule_reason ? (
                <div style={C.note}>
                  <div style={C.noteLabel}>RESCHEDULED{job.reschedule_count ? ` ×${job.reschedule_count}` : ''}</div>
                  <div style={C.noteText}>
                    {job.reschedule_reason}
                    {job.original_date ? ` (originally ${fmtDateOnly(job.original_date)})` : ''}
                  </div>
                </div>
              ) : null}

              {/* ── progress ──────────────────────────────────────── */}
              <div style={C.sectionTitle}>PROGRESS</div>
              <div style={C.timeline}>
                <Step label="Scheduled" when={fmtDateOnly(job.scheduled_date)} done={!!job.scheduled_date} />
                <Step label="Started" when={fmtPKTDateTime(job.started_at)} done={!!job.started_at} />
                <Step label="Before photos" when={fmtPKTDateTime(job.before_photos_at)} done={!!job.before_photos_at} />
                <Step label="After photos" when={fmtPKTDateTime(job.after_photos_at)} done={!!job.after_photos_at} />
                <Step label="Completed" when={fmtPKTDateTime(job.completed_at)} done={!!job.completed_at} />
              </div>

              {/* ── photos ────────────────────────────────────────── */}
              {before.length > 0 || after.length > 0 ? (
                <>
                  <div style={C.sectionTitle}>PHOTOS</div>
                  <PhotoGroup
                    title="Before"
                    at={job.before_photos_at}
                    urls={before}
                    onOpen={(i) => setLightbox({ urls: before, index: i })}
                  />
                  <PhotoGroup
                    title="After"
                    at={job.after_photos_at}
                    urls={after}
                    onOpen={(i) => setLightbox({ urls: after, index: i })}
                  />
                </>
              ) : (
                <>
                  <div style={C.sectionTitle}>PHOTOS</div>
                  <div style={C.state}>The crew did not upload photos for this visit.</div>
                </>
              )}

              {/* ── FSR ───────────────────────────────────────────── */}
              {findings.length > 0 ? (
                <>
                  <div style={C.sectionTitle}>WHAT THE CREW FOUND</div>
                  <div style={C.findings}>
                    {findings.map(([label, value]) => (
                      <div key={label} style={C.findingRow}>
                        <span style={C.findingLabel}>{label}</span>
                        <span style={C.findingValue}>{findingValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}

              {job.additional_notes ? (
                <div style={C.note}>
                  <div style={C.noteLabel}>CREW NOTES</div>
                  <div style={C.noteText}>{job.additional_notes}</div>
                </div>
              ) : null}

              {job.notes ? (
                <div style={C.note}>
                  <div style={C.noteLabel}>SCHEDULE NOTES</div>
                  <div style={C.noteText}>{job.notes}</div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {lightbox ? (
        <div style={C.lightWrap} onClick={(e) => { e.stopPropagation(); setLightbox(null); }}>
          <img src={lightbox.urls[lightbox.index]} alt="" style={C.lightImg} onClick={(e) => e.stopPropagation()} />
          <div style={C.lightBar} onClick={(e) => e.stopPropagation()}>
            <button
              style={C.lightBtn}
              onClick={() => setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.urls.length) % lb.urls.length }))}
            >
              ←
            </button>
            <span style={C.lightCount}>{lightbox.index + 1} / {lightbox.urls.length}</span>
            <button
              style={C.lightBtn}
              onClick={() => setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.urls.length }))}
            >
              →
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Fact({ label, value, color }) {
  return (
    <div style={C.fact}>
      <div style={C.factLabel}>{label}</div>
      <div style={{ ...C.factValue, ...(color ? { color } : null) }}>{value}</div>
    </div>
  );
}

function Step({ label, when, done }) {
  return (
    <div style={C.step}>
      <span style={{ ...C.stepDot, ...(done ? { background: T.tealLight, borderColor: T.tealLight } : null) }} />
      <span style={{ ...C.stepLabel, ...(done ? { color: T.textStrong } : null) }}>{label}</span>
      <span style={C.stepWhen}>{done ? when : 'Pending'}</span>
    </div>
  );
}

function PhotoGroup({ title, at, urls, onOpen }) {
  if (urls.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={C.groupHead}>
        <span style={C.groupTitle}>{title}</span>
        <span style={C.groupMeta}>{urls.length} · {at ? fmtPKTDateTime(at) : '—'}</span>
      </div>
      <div style={C.grid}>
        {urls.map((u, i) => (
          <img
            key={`${u}-${i}`}
            src={u}
            alt={`${title} ${i + 1}`}
            style={C.gridImg}
            loading="lazy"
            onClick={() => onOpen(i)}
          />
        ))}
      </div>
    </div>
  );
}

const C = {
  // ── card ──
  card: {
    width: 268,
    marginBottom: 6,
    borderRadius: 12,
    overflow: 'hidden',
    background: 'var(--nd-w05)',
    border: `1px solid ${T.border}`,
  },
  cardMine: { background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)' },
  strip: { display: 'grid', gap: 2, background: 'rgba(0,0,0,0.25)' },
  thumbWrap: { position: 'relative', paddingTop: '78%', overflow: 'hidden' },
  thumb: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  more: {
    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 800,
  },
  cardBody: { padding: '8px 10px 9px' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 6 },
  cardTitle: {
    flex: 1, fontSize: 12.5, fontWeight: 800, color: T.textStrong,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  dot: { width: 6, height: 6, borderRadius: 3, flexShrink: 0 },
  cardStatus: { fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' },
  cardMeta: { fontSize: 11, color: T.textMuted, marginTop: 2, fontWeight: 600 },
  warn: { fontSize: 10.5, color: T.amber, fontWeight: 700, marginTop: 4, lineHeight: 1.35 },
  cardLink: { fontSize: 11, fontWeight: 800, color: T.tealLight, marginTop: 6 },
  cardMutedLink: { fontSize: 11, fontWeight: 700, color: T.textMuted, marginTop: 6 },

  // ── modal ──
  wrap: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 80,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modal: {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16,
    width: 'min(760px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif", color: T.text,
  },
  modalHead: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
    padding: '16px 18px', borderBottom: `1px solid ${T.border}`,
  },
  modalTitle: { fontSize: 17, fontWeight: 800, color: T.textStrong },
  modalSub: { fontSize: 12, color: T.textMuted, marginTop: 3 },
  close: {
    background: 'var(--nd-w08)', border: 'none', color: T.text, borderRadius: 8,
    width: 30, height: 30, cursor: 'pointer', fontSize: 13, flexShrink: 0,
  },
  modalBody: { padding: 18, overflowY: 'auto' },
  state: { padding: '18px 0', fontSize: 12.5, color: T.textMuted },
  stateError: { padding: '18px 0', fontSize: 12.5, color: T.red },

  factGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 },
  fact: { background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 10px' },
  factLabel: { fontSize: 9.5, fontWeight: 800, color: T.textMuted, letterSpacing: 0.7 },
  factValue: { fontSize: 13, fontWeight: 700, color: T.textStrong, marginTop: 3 },

  warnBlock: {
    marginTop: 12, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
    color: T.amber, borderRadius: 10, padding: '9px 12px', fontSize: 12, lineHeight: 1.45, fontWeight: 600,
  },

  sectionTitle: {
    fontSize: 10, fontWeight: 800, color: T.textMuted, letterSpacing: 1,
    marginTop: 20, marginBottom: 10,
  },

  timeline: { display: 'flex', flexDirection: 'column', gap: 8 },
  step: { display: 'flex', alignItems: 'center', gap: 10 },
  stepDot: {
    width: 9, height: 9, borderRadius: 5, border: `2px solid ${T.border}`,
    background: 'transparent', flexShrink: 0,
  },
  stepLabel: { flex: 1, fontSize: 12.5, fontWeight: 700, color: T.textMuted },
  stepWhen: { fontSize: 11.5, color: T.textMuted, fontWeight: 600 },

  groupHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 },
  groupTitle: { fontSize: 12.5, fontWeight: 800, color: T.textStrong },
  groupMeta: { fontSize: 11, color: T.textMuted, fontWeight: 600 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 },
  gridImg: {
    width: '100%', height: 110, objectFit: 'cover', borderRadius: 10,
    cursor: 'zoom-in', display: 'block', background: 'var(--nd-w05)',
  },

  findings: { display: 'flex', flexDirection: 'column' },
  findingRow: {
    display: 'flex', justifyContent: 'space-between', gap: 16,
    padding: '8px 0', borderBottom: `1px solid ${T.border}`,
  },
  findingLabel: { fontSize: 12.5, color: T.textMuted, fontWeight: 700 },
  findingValue: { fontSize: 12.5, color: T.textStrong, fontWeight: 700, textAlign: 'right' },

  note: { marginTop: 14, background: 'var(--nd-w05)', borderRadius: 10, padding: 12 },
  noteLabel: { fontSize: 9.5, fontWeight: 800, color: T.textMuted, letterSpacing: 0.8 },
  noteText: { fontSize: 12.5, lineHeight: 1.5, color: T.text, marginTop: 4, whiteSpace: 'pre-wrap' },

  // ── lightbox ──
  lightWrap: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 90,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
  },
  lightImg: { maxWidth: '92vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 },
  lightBar: { display: 'flex', alignItems: 'center', gap: 16 },
  lightBtn: {
    background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', borderRadius: 8,
    width: 38, height: 34, cursor: 'pointer', fontSize: 16,
  },
  lightCount: { color: '#fff', fontSize: 12.5, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" },
};
